/**
 * @file routes/_app/me/report-styles/$id.tsx
 * @description 報告樣式模板編輯頁（M2）。
 *
 * 功能：
 * - 顯示並編輯模板：名稱、描述、提示詞（promptDirectives）
 * - 多選範例報告 IDs（exampleReportIds）
 * - 儲存按鈕（PATCH /api/me/report-styles/:id）
 * - 設為預設按鈕
 *
 * 路由特殊值：
 * - id = 'new' 時為建立模式（POST）；「建立模板」按鈕直接導向 `/me/report-styles/$id`（id='new'）。
 *   ⚠️ 序43（2026-09-25）：原另有 `new.tsx` 靜態路由把 /new 重定向到 $id(new)，而產生的 URL
 *   仍是 /new → 再次命中靜態路由 → 無限重定向，瀏覽器提示「網頁無回應」。已刪除 new.tsx。
 *
 * 視覺：PageShell + SectionCard + FormSection
 *
 * 角色：
 * - 需要 report-style.manage 能力（auditor / manager）
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Form, Input, Select, Space, Switch, Tag } from 'antd';
import { message } from '@/lib/antd-app';
import { ArrowLeft, Save, Star } from 'lucide-react';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { PageShell, SectionCard, FormSection } from '@/components/shell';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/me/report-styles/$id')({
  component: ReportStyleEditPage,
});

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface ReportStyleTemplate {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  exampleReportIds: number[] | null;
  promptDirectives: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditReport {
  id: number;
  code: string;
}

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 報告樣式模板編輯頁面。
 */
function ReportStyleEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const isNew = id === 'new';

  // ─── 查詢現有模板（編輯模式） ─────────────────────────────────────
  const { data, isLoading } = useQuery<{ item: ReportStyleTemplate }>({
    queryKey: ['report-style', id],
    queryFn: () => apiClient.get(`/api/me/report-styles/${id}`),
    enabled: !isNew,
  });

  // ─── 查詢可選的範例報告列表 ───────────────────────────────────────
  const { data: reportsData } = useQuery<{ items: AuditReport[] }>({
    queryKey: ['audit-reports-options'],
    queryFn: () => apiClient.get('/api/audit-reports?pageSize=100'),
  });

  const template = data?.item;
  const reportOptions = (reportsData?.items ?? []).map((r) => ({
    value: r.id,
    label: r.code,
  }));

  /** 編輯模式：填入現有值 */
  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        name: template.name,
        description: template.description ?? '',
        isDefault: template.isDefault,
        exampleReportIds: template.exampleReportIds ?? [],
        promptDirectives: template.promptDirectives ?? '',
      });
    }
  }, [template, form]);

  // ─── 建立模板 ────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: unknown) => apiClient.post('/api/me/report-styles', body),
    onSuccess: (result) => {
      message.success('模板已建立');
      void qc.invalidateQueries({ queryKey: ['report-styles'] });
      // apiClient 已解包 successResponse 的 data，回傳即 `{ item }`（序43：原誤取 data.item 恆為 undefined）
      const newId = (result as { item?: { id?: number } } | undefined)?.item?.id;
      if (newId) {
        void navigate({ to: '/me/report-styles/$id', params: { id: String(newId) } });
      } else {
        void navigate({ to: '/me/report-styles' });
      }
    },
    onError: (e: Error) => message.error(e.message),
  });

  // ─── 更新模板 ────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: (body: unknown) => apiClient.patch(`/api/me/report-styles/${id}`, body),
    onSuccess: () => {
      message.success('模板已儲存');
      void qc.invalidateQueries({ queryKey: ['report-styles'] });
      void qc.invalidateQueries({ queryKey: ['report-style', id] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // ─── 設為預設 ────────────────────────────────────────────────────
  const setDefaultMutation = useMutation({
    mutationFn: () => apiClient.patch(`/api/me/report-styles/${id}/set-default`, {}),
    onSuccess: () => {
      message.success('已設為預設模板');
      void qc.invalidateQueries({ queryKey: ['report-styles'] });
      void qc.invalidateQueries({ queryKey: ['report-style', id] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const handleSubmit = (values: {
    name: string;
    description: string;
    isDefault: boolean;
    exampleReportIds: number[];
    promptDirectives: string;
  }) => {
    const body = {
      ...values,
      description: values.description || null,
      promptDirectives: values.promptDirectives || null,
      exampleReportIds: values.exampleReportIds?.length ? values.exampleReportIds : null,
    };
    if (isNew) {
      createMutation.mutate(body);
    } else {
      updateMutation.mutate(body);
    }
  };

  if (!isNew && isLoading) return <div className="p-6">載入中...</div>;
  if (!isNew && !isLoading && !template) {
    return (
      <div className="p-6">
        <Alert type="error" message="模板不存在或無權存取" />
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell
      title={isNew ? '建立報告樣式模板' : `編輯模板：${template?.name ?? ''}`}
      description="設定 AI 報告生成時使用的提示詞與參考範例報告"
      actions={
        <Button
          icon={<ArrowLeft size={14} />}
          onClick={() => void navigate({ to: '/me/report-styles' })}
        >
          返回列表
        </Button>
      }
    >
      <div style={{ maxWidth: 800 }}>
        <SectionCard>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <FormSection title="基本資訊" description="模板名稱與用途說明" bordered>
              {/* 模板名稱 */}
              <Form.Item
                name="name"
                label="模板名稱"
                rules={[{ required: true, message: '請輸入模板名稱' }]}
              >
                <Input placeholder="例如：有機農業標準稽核報告" />
              </Form.Item>

              <Form.Item name="description" label="描述">
                <Input.TextArea rows={2} placeholder="此模板的用途說明..." />
              </Form.Item>

              {/* 設為預設 */}
              <Form.Item name="isDefault" label="設為預設模板" valuePropName="checked">
                <Switch />
              </Form.Item>
            </FormSection>

            <FormSection title="AI 生成設定" description="配置 AI 生成報告時的提示詞與參考範例">
              {/* 提示詞指令 */}
              <Form.Item
                name="promptDirectives"
                label={
                  <Space>
                    AI 提示詞指令
                    <Tag color="purple">影響 AI 草稿生成</Tag>
                  </Space>
                }
                extra="輸入對 AI 的具體指示，例如：語氣要正式、強調食品安全法規合規性、使用台灣有機農業相關術語"
              >
                <Input.TextArea
                  rows={6}
                  placeholder="例如：
- 使用繁體中文，語氣專業正式
- 優先引用 MOA 有機農業標準
- 表格格式呈現主要發現
- 重點強調改善建議"
                />
              </Form.Item>

              {/* 範例報告 IDs */}
              <Form.Item
                name="exampleReportIds"
                label={
                  <Space>
                    範例報告
                    <Tag color="blue">AI 學習參考</Tag>
                  </Space>
                }
                extra="選擇過去的優質報告作為 AI 生成的參考範本"
              >
                <Select
                  mode="multiple"
                  placeholder="選擇報告作為範例（可多選）"
                  options={reportOptions}
                  optionFilterProp="label"
                  showSearch
                />
              </Form.Item>
            </FormSection>

            {/* 操作按鈕 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isPending}
                icon={<Save size={14} />}
              >
                {isNew ? '建立模板' : '儲存變更'}
              </Button>
              {!isNew && !template?.isDefault && (
                <Button
                  icon={<Star size={14} />}
                  loading={setDefaultMutation.isPending}
                  onClick={() => setDefaultMutation.mutate()}
                >
                  設為預設
                </Button>
              )}
              <Button onClick={() => void navigate({ to: '/me/report-styles' })}>取消</Button>
            </div>
          </Form>
        </SectionCard>
      </div>
    </PageShell>
  );
}
